import React, { useState } from "react";
import { Bug, Loader2, X, Send, Camera, ImageOff } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { submitFeedback } from "../lib/api";

/**
 * V1.9.0 — Beta feedback button + modal.
 *
 * Floating cyan FAB in the bottom-right of every authenticated page.
 * Opens a small modal where the user picks a category + severity,
 * writes a message, and submits. We auto-capture page URL, user agent,
 * viewport, and (when present) the design_id from the URL path so the
 * admin inbox has enough context to reproduce the issue without
 * pestering the user.
 *
 * Intentionally minimal — no screenshot capture from the live canvas
 * (would risk hanging on WebGL contexts); admin can repro via the
 * page_url field. The screenshot_data_url backend field stays available
 * for a future "Attach screenshot" upload control.
 */
const CATEGORIES = [
  { id: "bug",     label: "Bug",     hint: "Something broke or looks wrong" },
  { id: "feature", label: "Feature", hint: "Something missing you'd love" },
  { id: "general", label: "General", hint: "Anything else" },
  { id: "praise",  label: "Praise",  hint: "Tell us what's working" },
];

const SEVERITIES = ["low", "normal", "high", "blocker"];

const extractDesignId = () => {
  const m = window.location.pathname.match(/\/designs\/([^/?#]+)/);
  return m ? m[1] : null;
};

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [severity, setSeverity] = useState("normal");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // V1.9.1 — Screenshot capture state. The user MUST close the feedback
  // modal during capture (the modal itself would otherwise be in the
  // screenshot) — html2canvas runs on `document.body` after the modal
  // is hidden, then re-opens the modal and shows a thumbnail preview.
  const [screenshot, setScreenshot] = useState(null); // data URL
  const [capturing, setCapturing] = useState(false);

  const reset = () => {
    setOpen(false);
    setMessage("");
    setSeverity("normal");
    setCategory("bug");
    setSubmitting(false);
    setScreenshot(null);
    setCapturing(false);
  };

  const handleCaptureScreenshot = async () => {
    setCapturing(true);
    // Hide the modal scrim so it doesn't appear in the screenshot.
    const scrim = document.querySelector(
      "[data-testid='feedback-modal-scrim']",
    );
    const fab = document.querySelector("[data-testid='feedback-fab']");
    const prevScrimDisplay = scrim?.style.display;
    const prevFabDisplay = fab?.style.display;
    if (scrim) scrim.style.display = "none";
    if (fab) fab.style.display = "none";
    // Let the browser repaint before capturing.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const canvas = await html2canvas(document.body, {
        backgroundColor: "#09090b",
        useCORS: true,
        logging: false,
        // Cap output size so the data URL stays well under 2MB.
        scale: Math.min(1, 1600 / window.innerWidth),
      });
      const dataUrl = canvas.toDataURL("image/jpeg", 0.72);
      // Truncation guard — backend enforces 2,000,000 char max.
      if (dataUrl.length > 1_950_000) {
        toast.warning("Screenshot too large — re-capturing at lower quality");
        const smallerUrl = canvas.toDataURL("image/jpeg", 0.4);
        setScreenshot(smallerUrl.length > 1_950_000 ? null : smallerUrl);
      } else {
        setScreenshot(dataUrl);
      }
    } catch (e) {
      toast.error("Couldn't capture screenshot — please try again");
    } finally {
      if (scrim) scrim.style.display = prevScrimDisplay || "";
      if (fab) fab.style.display = prevFabDisplay || "";
      setCapturing(false);
    }
  };

  const handleSubmit = async () => {
    const text = message.trim();
    if (text.length < 3) {
      toast.error("Please describe your feedback (3+ chars)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        message: text,
        category,
        severity,
        design_id: extractDesignId(),
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        // V1.9.1 — Attach the captured screenshot if the user added one.
        screenshot_data_url: screenshot || null,
      };
      const r = await submitFeedback(payload);
      const id = r?.data?.feedback_id || "submitted";
      toast.success(`Thanks! Feedback ${id.slice(0, 8)}… recorded`);
      reset();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Couldn't send feedback. Please try again in a moment.",
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="feedback-fab"
        onClick={() => setOpen(true)}
        title="Beta feedback"
        /* V2.0.5 — hidden on phones to free up the bottom-right corner
           for the MobileChatDock send button. Tablet+ keeps the FAB. */
        className="hidden sm:inline-flex fixed bottom-5 right-5 md:right-[19rem] z-50 items-center gap-1.5 px-3 py-2 bg-brass-600 hover:bg-brass-500 text-zinc-950 font-mono text-[10px] uppercase tracking-widest shadow-[0_6px_24px_rgba(196,169,107,0.45)] transition-colors"
      >
        <Bug className="w-3.5 h-3.5" /> Feedback
      </button>

      {open && (
        <div
          data-testid="feedback-modal-scrim"
          className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/85 backdrop-blur-sm p-4"
          onClick={() => !submitting && reset()}
        >
          <div
            data-testid="feedback-modal"
            className="w-full max-w-md bg-zinc-950 border-2 border-brass-600 shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
                  // BETA · FEEDBACK
                </div>
                <h3 className="font-display text-lg text-zinc-50 mt-0.5">
                  Tell us what you found
                </h3>
              </div>
              <button
                type="button"
                onClick={reset}
                disabled={submitting}
                className="text-zinc-400 hover:text-zinc-100 disabled:opacity-50"
                data-testid="feedback-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  Category
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`feedback-cat-${c.id}`}
                      onClick={() => setCategory(c.id)}
                      aria-pressed={category === c.id}
                      className={
                        "px-2 py-2 border text-left font-mono text-[10px] uppercase tracking-wider transition-colors " +
                        (category === c.id
                          ? "border-brass-500 bg-brass-950 text-brass-100"
                          : "border-zinc-700 text-zinc-400 hover:border-zinc-500")
                      }
                    >
                      <div className="text-zinc-100 font-semibold">{c.label}</div>
                      <div className="text-[9px] text-zinc-500 normal-case tracking-normal mt-0.5">
                        {c.hint}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  Severity
                </div>
                <div className="inline-flex border border-zinc-700">
                  {SEVERITIES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`feedback-sev-${s}`}
                      onClick={() => setSeverity(s)}
                      aria-pressed={severity === s}
                      className={
                        "px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border-r border-zinc-700 last:border-r-0 transition-colors " +
                        (severity === s
                          ? s === "blocker"
                            ? "bg-orange-600 text-white"
                            : s === "high"
                              ? "bg-amber-600 text-zinc-950"
                              : "bg-brass-600 text-zinc-950"
                          : "text-zinc-400 hover:text-zinc-100")
                      }
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  Message
                </div>
                <textarea
                  data-testid="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What happened? What did you expect?"
                  rows={5}
                  maxLength={4000}
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-brass-500 focus:outline-none px-3 py-2 text-zinc-100 text-sm font-mono placeholder:text-zinc-600"
                />
                <div className="font-mono text-[9px] text-zinc-600 mt-1">
                  {message.length}/4000 · page + viewport auto-attached
                </div>
              </div>

              {/* V1.9.1 — Optional screenshot attachment. Captures the
                  current page (sans the feedback modal) via html2canvas
                  and stores as a data URL on the feedback record. */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center justify-between">
                  <span>Screenshot (optional)</span>
                  {screenshot && (
                    <button
                      type="button"
                      onClick={() => setScreenshot(null)}
                      data-testid="feedback-screenshot-clear"
                      className="text-orange-400 hover:text-orange-300 normal-case tracking-normal text-[10px] inline-flex items-center gap-1"
                    >
                      <ImageOff className="w-3 h-3" /> remove
                    </button>
                  )}
                </div>
                {screenshot ? (
                  <div
                    data-testid="feedback-screenshot-preview"
                    className="relative border border-zinc-700 bg-zinc-900"
                  >
                    <img
                      src={screenshot}
                      alt="Captured screenshot"
                      className="w-full h-32 object-cover"
                    />
                    <div className="px-2 py-1 border-t border-zinc-800 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                      // {Math.round(screenshot.length / 1024)} KB · attached
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="feedback-screenshot-capture"
                    onClick={handleCaptureScreenshot}
                    disabled={capturing}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed border-zinc-700 hover:border-brass-500 hover:bg-brass-950/20 text-zinc-400 hover:text-brass-300 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
                  >
                    {capturing ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" /> Capturing…
                      </>
                    ) : (
                      <>
                        <Camera className="w-3 h-3" /> Capture current page
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-between">
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                Thanks for testing 🛠
              </div>
              <button
                type="button"
                data-testid="feedback-submit"
                onClick={handleSubmit}
                disabled={submitting || message.trim().length < 3}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-brass-600 hover:bg-brass-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-zinc-950 font-mono text-[10px] uppercase tracking-widest"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-3 h-3" /> Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
